"use client"

import { axiosAuth } from "@/lib/axios"
import { useSession } from "next-auth/react"
import { useEffect, useState } from "react"
import useRefreshToken from "./useRefreshToken"

import { LOADING, UNAUTHENTICATED } from "@/constants/ConstantSession"

const useAxiosAuth = () => {
	const { data: session, status: sessionStatus } = useSession()
	const refreshToken = useRefreshToken()
	const [isAxiosAuthReady, setIsAxiosAuthReady] = useState(false)

	useEffect(() => {
		if (sessionStatus === LOADING) return
		if (sessionStatus === UNAUTHENTICATED) {
			setIsAxiosAuthReady(true)
			return
		}

		const requestIntercept = axiosAuth.interceptors.request.use(
			(config) => {
				if (!config.headers["Authorization"]) {
					config.headers["Authorization"] = `Bearer ${
						session?.user?.accessToken ?? session?.accessToken
					}`
				}

				return config
			},
			(error) => Promise.reject(error)
		)

		const responseIntercept = axiosAuth.interceptors.response.use(
			(response) => response,
			async (error) => {
				const prevRequest = error.config
				if (error.response.status === 401 && !prevRequest.sent) {
					prevRequest.sent = true

					await refreshToken()
					prevRequest.headers[
						"Authorization"
					] = `Bearer ${session?.user?.accessToken}`
					return axiosAuth(prevRequest)
				}

				return Promise.reject(error)
			}
		)

		setIsAxiosAuthReady(true)

		return () => {
			axiosAuth.interceptors.request.eject(requestIntercept)
			axiosAuth.interceptors.response.eject(responseIntercept)
		}
	}, [session, refreshToken, sessionStatus])

	return { axiosAuth, isAxiosAuthReady }
}

export default useAxiosAuth
